/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 */

import { isKnetEmbed } from "app/client/lib/knetEmbed";
import { hoverTooltip } from "app/client/ui/tooltips";
import { testId, theme } from "app/client/ui2018/cssVars";
import { IconName } from "app/client/ui2018/IconList";
import { icon } from "app/client/ui2018/icons";
import { RAIL_INFO } from "app/common/knetRailInfo";

import { DomElementArg, styled } from "grainjs";

/**
 * In Kue the left pane is the 48 px rail and never opens, so each icon shows its
 * info card on hover, as on every KNet service: the icon, name and one line
 * (hermes-agent-ui openspec unify-embed-rail-and-signin). Outside Kue hovering the
 * collapsed pane opens it, so no card is needed.
 */
export function railCard(id: string): DomElementArg {
  const info = RAIL_INFO[id];
  if (!info || !isKnetEmbed()) { return null; }
  return hoverTooltip(() => cssRailCard(
    // The tooltip box is dark by default; the card brings its own light surface.
    (elem: HTMLElement) => { setTimeout(() => { elem.parentElement?.classList.add(cssRailCardHost.className); }); },
    cssRailCardName(icon(info.icon as IconName), info.title),
    cssRailCardLine(info.line),
    testId("rail-card"),
  ), { placement: "right", key: "railCard" });
}

const cssRailCardHost = styled("div", `
  background-color: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
`);

const cssRailCard = styled("div", `
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 210px;
  padding: 6px 12px;
  border: 1px solid ${theme.menuBorder};
  border-radius: 6px;
  background-color: ${theme.menuBg};
  color: ${theme.text};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  text-align: left;
`);

const cssRailCardName = styled("div", `
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  & > .test-icon, & > div:first-child {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    --icon-color: ${theme.controlPrimaryBg};
  }
`);

const cssRailCardLine = styled("div", `
  font-size: 11px;
  line-height: 1.35;
  color: ${theme.lightText};
`);
